import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { adminAPI } from "../api";
import { getUserErrorMessage } from "../utils/errors";
import MIcon from "../icons/MIcon";
import SpinnerIcon from "../icons/SpinnerIcon";

const UPON_QUOTATION = "upon_quotation";

const ORG_TYPES = ["tennis_instructor", "bookshop", "barber_shop", "plumber"];

const emptyService = () => ({
  name: "",
  description: "",
  price: "",
  uponQuotation: false,
  duration: "",
});

/**
 * Convert a service coming from the API into the shape used by the form.
 * The API returns ``price`` as either a number or the literal
 * ``"upon_quotation"`` sentinel; the form splits that into
 * ``{ price: string, uponQuotation: boolean }``.
 */
const serviceFromApi = (svc) => ({
  name: svc.name || "",
  description: svc.description || "",
  price:
    svc.price === UPON_QUOTATION || svc.price == null
      ? ""
      : String(svc.price),
  uponQuotation: svc.price === UPON_QUOTATION,
  duration: svc.duration == null ? "" : String(svc.duration),
});

/**
 * Build the JSON payload sent to the backend.  Applies the toggle-based
 * clearing rules so the request always matches the server-side validation
 * (mandatory_price / mandatory_duration).
 */
const buildOrgPayload = (form) => {
  const services = form.services
    .filter((s) => s.name.trim())
    .map((s) => {
      const entry = {
        name: s.name.trim(),
        description: s.description.trim() || null,
        price: null,
        duration: null,
      };
      if (form.enablePrices) {
        if (s.uponQuotation) {
          entry.price = UPON_QUOTATION;
        } else if (s.price !== "" && s.price != null) {
          const num = parseFloat(s.price);
          entry.price = Number.isFinite(num) ? num : null;
        }
      }
      if (form.enableDurations) {
        if (s.duration !== "" && s.duration != null) {
          const dur = parseInt(s.duration, 10);
          entry.duration = Number.isFinite(dur) ? dur : null;
        }
      }
      return entry;
    });

  return {
    orgname: form.orgname.trim(),
    phone: form.phone.trim(),
    email: form.email.trim() || null,
    address: form.address.trim() || null,
    org_type: form.orgType || null,
    description: form.description.trim() || null,
    services,
    services_config: {
      mandatory_price: !!form.enablePrices,
      mandatory_duration: !!form.enableDurations,
    },
  };
};

const emptyForm = () => ({
  orgname: "",
  phone: "",
  email: "",
  address: "",
  orgType: "",
  description: "",
  enablePrices: false,
  enableDurations: false,
  services: [],
});

const formFromOrg = (org) => ({
  orgname: org.orgname || "",
  phone: org.phone || "",
  email: org.email || "",
  address: org.address || "",
  orgType: org.org_type || "",
  description: org.description || "",
  enablePrices: !!org.services_config?.mandatory_price,
  enableDurations: !!org.services_config?.mandatory_duration,
  services: (org.services || []).map(serviceFromApi),
});

// ─────────────────────────────────────────────────────────────────────
// Shared, controlled OrganizationForm
// ─────────────────────────────────────────────────────────────────────

const OrganizationForm = ({
  form,
  setForm,
  onSubmit,
  isSubmitting,
  submitLabel,
  submittingLabel,
  onCancel,
  cancelLabel,
  message,
  compact = false,
}) => {
  const { t } = useTranslation();

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const addService = () =>
    setForm((prev) => ({ ...prev, services: [...prev.services, emptyService()] }));

  const removeService = (idx) =>
    setForm((prev) => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== idx),
    }));

  const updateService = (idx, patch) =>
    setForm((prev) => {
      const services = [...prev.services];
      services[idx] = { ...services[idx], ...patch };
      return { ...prev, services };
    });

  /**
   * Toggle a top-level flag (enablePrices / enableDurations) and clear
   * the corresponding per-service values immediately when it is switched OFF.
   */
  const toggleEnable = (flag) =>
    setForm((prev) => {
      const nextValue = !prev[flag];
      let services = prev.services;
      if (!nextValue) {
        services = services.map((s) => {
          if (flag === "enablePrices") {
            return { ...s, price: "", uponQuotation: false };
          }
          if (flag === "enableDurations") {
            return { ...s, duration: "" };
          }
          return s;
        });
      }
      return { ...prev, [flag]: nextValue, services };
    });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* Row 1: Name + Phone */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
            <MIcon name="apartment" className="sm" />
          </div>
          <input
            type="text"
            placeholder={t("organizations.namePlaceholder")}
            value={form.orgname}
            onChange={(e) => setField("orgname", e.target.value)}
            required
            className="auth-input w-full pl-11 pr-4 py-3 rounded-lg text-sm font-body"
          />
        </div>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
            <MIcon name="call" className="sm" />
          </div>
          <input
            type="tel"
            placeholder={t("organizations.phonePlaceholder")}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            required
            className="auth-input w-full pl-11 pr-4 py-3 rounded-lg text-sm font-body"
          />
        </div>
      </div>

      {/* Row 2: Email + Address + Type */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
            <MIcon name="mail" className="sm" />
          </div>
          <input
            type="email"
            placeholder={t("organizations.emailPlaceholder")}
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            className="auth-input w-full pl-11 pr-4 py-3 rounded-lg text-sm font-body"
          />
        </div>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
            <MIcon name="location_on" className="sm" />
          </div>
          <input
            type="text"
            placeholder={t("organizations.addressPlaceholder")}
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            className="auth-input w-full pl-11 pr-4 py-3 rounded-lg text-sm font-body"
          />
        </div>
        <div className="relative">
          <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
            <MIcon name="category" className="sm" />
          </div>
          <select
            value={form.orgType}
            onChange={(e) => setField("orgType", e.target.value)}
            className="auth-input w-full pl-11 pr-4 py-3 rounded-lg text-sm font-body appearance-none"
          >
            <option value="">{t("organizations.typePlaceholder")}</option>
            {ORG_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`organizations.orgTypes.${type}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 3: Description */}
      <div className="relative">
        <textarea
          placeholder={t("organizations.descriptionPlaceholder")}
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          rows={3}
          className="auth-input w-full px-4 py-3 rounded-lg text-sm font-body resize-none"
        />
      </div>

      {/* ── Services Section ── */}
      <div className="border border-surface-light-highest dark:border-white/10 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-on-surface-light dark:text-on-surface">
            {t("organizations.services")}
          </h3>
          <div className="flex items-center gap-4">
            <ToggleSwitch
              checked={form.enablePrices}
              onChange={() => toggleEnable("enablePrices")}
              label={t("organizations.enablePrices")}
            />
            <ToggleSwitch
              checked={form.enableDurations}
              onChange={() => toggleEnable("enableDurations")}
              label={t("organizations.enableDurations")}
            />
            <button
              type="button"
              onClick={addService}
              className="flex items-center gap-1 text-xs text-brand dark:text-brand-light hover:underline"
            >
              <MIcon name="add" className="sm" />
              {t("organizations.addService")}
            </button>
          </div>
        </div>

        {form.services.length === 0 && (
          <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant">
            {t("organizations.noServices")}
          </p>
        )}

        {form.services.map((svc, idx) => (
          <ServiceRow
            key={idx}
            svc={svc}
            enablePrices={form.enablePrices}
            enableDurations={form.enableDurations}
            onChange={(patch) => updateService(idx, patch)}
            onRemove={() => removeService(idx)}
          />
        ))}
      </div>

      {/* Submit / Cancel */}
      <div
        className={`flex ${
          onCancel ? "justify-between" : "justify-end"
        } items-center gap-2`}
      >
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="text-sm px-4 py-2 rounded-lg border border-surface-light-highest dark:border-white/10 text-on-surface-light dark:text-on-surface hover:bg-surface-light-container dark:hover:bg-surface-container transition-colors disabled:opacity-40"
          >
            {cancelLabel}
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-brand dark:bg-brand-container hover:bg-brand-light dark:hover:bg-brand text-white font-semibold py-3 px-5 rounded-lg transition-all duration-200 shadow-brand-glow disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm tracking-wide active:scale-95 whitespace-nowrap"
        >
          {isSubmitting ? (
            <>
              <SpinnerIcon />
              <span>{submittingLabel}</span>
            </>
          ) : (
            <>
              <MIcon name={compact ? "save" : "add"} className="sm" />
              <span>{submitLabel}</span>
            </>
          )}
        </button>
      </div>

      {/* Message */}
      {message && message.text && (
        <div
          className={`rounded-lg px-4 py-3 text-sm flex items-start gap-2.5 ${
            message.type === "success"
              ? "bg-emerald-500/10 dark:bg-emerald-400/10 border border-emerald-500/20 dark:border-emerald-400/20 text-emerald-600 dark:text-emerald-400"
              : "bg-red-500/10 dark:bg-red-400/10 border border-red-500/20 dark:border-red-400/20 text-red-600 dark:text-red-400"
          }`}
        >
          <span className="mt-0.5 shrink-0">
            <MIcon
              name={message.type === "success" ? "check_circle" : "error"}
              className="sm"
            />
          </span>
          <span>{message.text}</span>
        </div>
      )}
    </form>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────

const ToggleSwitch = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2 text-xs text-on-surface-light-variant dark:text-on-surface-variant cursor-pointer select-none">
    <span
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onChange();
        }
      }}
      tabIndex={0}
      className={`relative inline-block w-8 h-4 rounded-full transition-colors ${
        checked
          ? "bg-brand dark:bg-brand-container"
          : "bg-surface-light-highest dark:bg-white/10"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 h-3 w-3 bg-white rounded-full shadow transition-transform ${
          checked ? "translate-x-4" : ""
        }`}
      />
    </span>
    <span>{label}</span>
  </label>
);

const ServiceRow = ({
  svc,
  enablePrices,
  enableDurations,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto_auto] gap-2 items-start">
      <input
        type="text"
        placeholder={t("organizations.serviceNamePlaceholder")}
        value={svc.name}
        onChange={(e) => onChange({ name: e.target.value })}
        className="auth-input w-full px-3 py-2 rounded-lg text-sm font-body"
      />
      <input
        type="text"
        placeholder={t("organizations.serviceDescPlaceholder")}
        value={svc.description}
        onChange={(e) => onChange({ description: e.target.value })}
        className="auth-input w-full px-3 py-2 rounded-lg text-sm font-body"
      />

      {/* Price cell */}
      {enablePrices ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            min="0"
            placeholder={t("organizations.servicePricePlaceholder")}
            value={svc.uponQuotation ? "" : svc.price}
            disabled={svc.uponQuotation}
            onChange={(e) => onChange({ price: e.target.value })}
            className="auth-input w-24 px-3 py-2 rounded-lg text-sm font-body disabled:opacity-50"
          />
          <label className="flex items-center gap-1 text-xs text-on-surface-light-variant dark:text-on-surface-variant cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={svc.uponQuotation}
              onChange={(e) =>
                onChange({
                  uponQuotation: e.target.checked,
                  price: e.target.checked ? "" : svc.price,
                })
              }
              className="cursor-pointer"
            />
            {t("organizations.uponQuotation")}
          </label>
        </div>
      ) : (
        <div />
      )}

      {/* Duration cell */}
      {enableDurations ? (
        <input
          type="number"
          step="1"
          min="0"
          placeholder={t("organizations.serviceDurationPlaceholder")}
          value={svc.duration}
          onChange={(e) => onChange({ duration: e.target.value })}
          className="auth-input w-28 px-3 py-2 rounded-lg text-sm font-body"
        />
      ) : (
        <div />
      )}

      <button
        type="button"
        onClick={onRemove}
        className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 p-2"
      >
        <MIcon name="delete" className="sm" />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Inline edit block used inside the expanded row
// ─────────────────────────────────────────────────────────────────────

const EditOrgInline = ({ org, onCancel, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => formFromOrg(org));
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if (!form.orgname.trim()) {
      setMessage({
        text: t("organizations.errors.nameRequired"),
        type: "error",
      });
      return;
    }
    if (!form.phone.trim()) {
      setMessage({
        text: t("organizations.errors.phoneRequired"),
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildOrgPayload(form);
      const res = await adminAPI.updateOrg(org.id, payload);
      setMessage({ text: t("organizations.updatedSuccess"), type: "success" });
      onSaved(res.data);
    } catch (err) {
      setMessage({ text: getUserErrorMessage(err, "update"), type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-3 ml-3 mr-3 border border-surface-light-highest dark:border-white/10 rounded-lg p-4">
      <OrganizationForm
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel={t("organizations.save")}
        submittingLabel={t("organizations.saving")}
        onCancel={onCancel}
        cancelLabel={t("organizations.cancel")}
        message={message}
        compact
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────

const CreateOrganization = () => {
  const { t } = useTranslation();
  const [currentOrgs, setCurrentOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState("");

  const [createForm, setCreateForm] = useState(emptyForm);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);

  const fetchOrgs = useCallback(async () => {
    setOrgsLoading(true);
    setOrgsError("");
    try {
      const res = await adminAPI.listOrgs();
      setCurrentOrgs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setOrgsError(getUserErrorMessage(err, "load"));
      setCurrentOrgs([]);
    } finally {
      setOrgsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    if (!createForm.orgname.trim()) {
      setMessage({
        text: t("organizations.errors.nameRequired"),
        type: "error",
      });
      return;
    }
    if (!createForm.phone.trim()) {
      setMessage({
        text: t("organizations.errors.phoneRequired"),
        type: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = buildOrgPayload(createForm);
      await adminAPI.createOrg(payload);
      setMessage({ text: t("organizations.createdSuccess"), type: "success" });
      setCreateForm(emptyForm());
      fetchOrgs();
    } catch (err) {
      setMessage({ text: getUserErrorMessage(err, "create"), type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaved = (updatedOrg) => {
    setCurrentOrgs((prev) =>
      prev.map((o) => (o.id === updatedOrg.id ? updatedOrg : o))
    );
    setEditingOrg(null);
  };

  const renderPriceLabel = (svc) => {
    if (svc.price === UPON_QUOTATION) {
      return t("organizations.uponQuotationShort");
    }
    if (svc.price == null) return null;
    const num = typeof svc.price === "number" ? svc.price : parseFloat(svc.price);
    return Number.isFinite(num) ? num.toFixed(2) : String(svc.price);
  };

  return (
    <div className="min-h-screen px-4 sm:px-8 py-10">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* ── Page Header ── */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-brand dark:bg-brand flex items-center justify-center shadow-brand-glow">
            <MIcon name="corporate_fare" className="text-white !text-[24px]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-on-surface-light dark:text-on-surface tracking-tight">
              {t("nav.organizations")}
            </h1>
            <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant">
              {t("organizations.subtitle")}
            </p>
          </div>
        </div>

        {/* ── Create Organization Card ── */}
        <div className="flat-card rounded-xl animate-float-in">
          <div className="p-6 sm:p-8">
            <h2 className="font-display text-base font-semibold text-on-surface-light dark:text-on-surface mb-4">
              {t("organizations.createNew")}
            </h2>

            <OrganizationForm
              form={createForm}
              setForm={setCreateForm}
              onSubmit={handleSubmit}
              isSubmitting={isLoading}
              submitLabel={t("organizations.create")}
              submittingLabel={t("organizations.creating")}
              message={message}
            />
          </div>
        </div>

        {/* ── Organizations List ── */}
        <div className="flat-card rounded-xl">
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-base font-semibold text-on-surface-light dark:text-on-surface">
                {t("organizations.existing")}
                {!orgsLoading && (
                  <span className="ml-2 text-xs font-normal text-on-surface-light-variant dark:text-on-surface-variant">
                    ({currentOrgs.length})
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={fetchOrgs}
                disabled={orgsLoading}
                className="flex items-center gap-1.5 text-xs text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors disabled:opacity-40"
              >
                <MIcon name="refresh" className="sm" />
                <span>{t("common.refresh")}</span>
              </button>
            </div>

            {orgsLoading ? (
              <div className="flex items-center justify-center py-12 text-on-surface-light-variant dark:text-on-surface-variant">
                <SpinnerIcon />
                <span className="ml-2 text-sm">
                  {t("organizations.loading")}
                </span>
              </div>
            ) : orgsError ? (
              <div className="rounded-lg px-4 py-3 text-sm flex items-start gap-2.5 bg-red-500/10 dark:bg-red-400/10 border border-red-500/20 dark:border-red-400/20 text-red-600 dark:text-red-400">
                <span className="mt-0.5 shrink-0">
                  <MIcon name="error" className="sm" />
                </span>
                <span>{orgsError}</span>
              </div>
            ) : currentOrgs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-on-surface-light-variant dark:text-on-surface-variant">
                <MIcon name="inbox" className="!text-[32px] mb-2 opacity-60" />
                <p className="text-sm">{t("organizations.empty")}</p>
                <p className="text-xs opacity-70">
                  {t("organizations.emptyHint")}
                </p>
              </div>
            ) : (
              <div className="space-y-0 divide-y divide-surface-light-highest dark:divide-white/5">
                {currentOrgs.map((org) => (
                  <div key={org.id} className="py-3">
                    {/* Org summary row */}
                    <div className="w-full flex items-center gap-4 text-left hover:bg-surface-light-container dark:hover:bg-surface-container rounded-lg px-3 py-2 transition-colors">
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedOrg(
                            expandedOrg === org.id ? null : org.id
                          );
                          if (editingOrg === org.id) setEditingOrg(null);
                        }}
                        className="flex-1 flex items-center gap-4 text-left"
                      >
                        <span className="text-on-surface-light-variant dark:text-on-surface-variant font-mono text-xs w-10">
                          #{org.id}
                        </span>
                        <span className="text-on-surface-light dark:text-on-surface font-medium flex-1">
                          {org.orgname}
                        </span>
                        {org.org_type && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-brand/10 dark:bg-brand/20 text-brand dark:text-brand-light">
                            {t(`organizations.orgTypes.${org.org_type}`)}
                          </span>
                        )}
                        <span className="text-on-surface-light-variant dark:text-on-surface-variant text-sm">
                          {org.phone}
                        </span>
                        <MIcon
                          name={
                            expandedOrg === org.id
                              ? "expand_less"
                              : "expand_more"
                          }
                          className="sm text-on-surface-light-variant dark:text-on-surface-variant"
                        />
                      </button>
                      {expandedOrg === org.id && editingOrg !== org.id && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingOrg(org.id);
                          }}
                          className="flex items-center gap-1 text-xs text-brand dark:text-brand-light hover:underline"
                        >
                          <MIcon name="edit" className="sm" />
                          {t("organizations.edit")}
                        </button>
                      )}
                    </div>

                    {/* Expanded details / edit form */}
                    {expandedOrg === org.id && editingOrg === org.id && (
                      <EditOrgInline
                        org={org}
                        onCancel={() => setEditingOrg(null)}
                        onSaved={handleSaved}
                      />
                    )}
                    {expandedOrg === org.id && editingOrg !== org.id && (
                      <div className="mt-2 ml-14 mr-3 space-y-3 text-sm text-on-surface-light-variant dark:text-on-surface-variant">
                        {org.email && (
                          <div className="flex items-center gap-2">
                            <MIcon name="mail" className="sm opacity-60" />
                            <span>{org.email}</span>
                          </div>
                        )}
                        {org.address && (
                          <div className="flex items-center gap-2">
                            <MIcon
                              name="location_on"
                              className="sm opacity-60"
                            />
                            <span>{org.address}</span>
                          </div>
                        )}
                        {org.description && (
                          <div className="flex items-start gap-2">
                            <MIcon
                              name="description"
                              className="sm opacity-60 mt-0.5"
                            />
                            <span>{org.description}</span>
                          </div>
                        )}
                        {org.services && org.services.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider mb-2 text-on-surface-light dark:text-on-surface">
                              {t("organizations.services")} (
                              {org.services.length})
                            </h4>
                            <div className="space-y-1">
                              {org.services.map((svc) => {
                                const priceLabel = renderPriceLabel(svc);
                                return (
                                  <div
                                    key={svc.id}
                                    className="flex items-center gap-3 text-sm bg-surface-light-container/50 dark:bg-surface-container/50 rounded-md px-3 py-1.5"
                                  >
                                    <span className="font-medium text-on-surface-light dark:text-on-surface">
                                      {svc.name}
                                    </span>
                                    {svc.description && (
                                      <span className="text-xs opacity-70">
                                        — {svc.description}
                                      </span>
                                    )}
                                    {svc.duration != null && (
                                      <span className="text-xs opacity-70">
                                        · {svc.duration}′
                                      </span>
                                    )}
                                    {priceLabel != null && (
                                      <span className="ml-auto text-xs font-mono text-brand dark:text-brand-light">
                                        {priceLabel}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {!org.email &&
                          !org.address &&
                          !org.description &&
                          (!org.services || org.services.length === 0) && (
                            <p className="text-xs opacity-60 italic">
                              {t("organizations.noDetails")}
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
